import React from 'react';
import styled, { useTheme } from 'styled-components';
import { ProgressBar } from 'react-step-progress-bar';
import { QUESTION_TYPES } from '../../../contexts/onboarding';
import Button from '../../elements/Button';
import InputQuestion from './InputQuestion';
import InputTimeTimezone from './InputTimeTimezone';
import MultiSelectQuestion from './MultiSelectQuestion';
import SingleSelectQuestion from './SingleSelectQuestion';
import useThrottledConsumer from '../../../hooks/use-throttled-consumer';
import { PSmall } from '../../elements/Text';
import autoAnimate from '@formkit/auto-animate';

const Wrapper = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: column;

  gap: 52px;
  padding-top: 52px;
`;

const AnimWrapper = styled.div`
  justify-content: center;
  align-items: center;
  display: flex;
  width: 100%;
  flex: 1;
`;
export function Question({
  questions,
  onNext,
  onPrev,
  onAnswerQuestion,
  currentIndex,
  isLoading = false,
}) {
  const question = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;

  const props = {
    index: currentIndex,
    question,
    onNext,
    onPrev,
    onAnswerQuestion,
    isLastQuestion,
    isLoading,
    // key: question.id,
  };

  if (question.type === QUESTION_TYPES.INPUT) {
    return <InputQuestion {...props} />;
  }
  if (question.type === QUESTION_TYPES.MULTI_SELECT) {
    return <MultiSelectQuestion {...props} />;
  }
  if (question.type === QUESTION_TYPES.SINGLE_SELECT) {
    return <SingleSelectQuestion {...props} />;
  }
  if (question.type === QUESTION_TYPES.INPUT_TIME_TIMEZONE) {
    return <InputTimeTimezone {...props} />;
  }
  return null;
}

const ProgressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FooterWrapper = styled.div`
  display: inline-block;
  position: relative;
  align-self: flex-end;
  flex-direction: row;
  justify-content: space-between;
  display: flex;
  width: 100%;
  padding-bottom: 23px;
`;

function Footer({ answeredCount, stepsCount, onGoBack, currentStep }) {
  const theme = useTheme();
  return (
    <FooterWrapper>
      <Button
        style={{ margin: 0, border: 0 }}
        color={theme.colors.secondary}
        isDisabled={currentStep === 0}
        onClick={onGoBack}
        label={'Go Back'}
        faIcon={'fa fa-arrow-left'}
        showIconBeforeLabel
      />
      <ProgressWrapper>
        <PSmall style={{ margin: 0, color: theme.colors.textRegular }}>
          {answeredCount} of {stepsCount} answered
        </PSmall>
        <ProgressBar
          height={2}
          percent={(answeredCount / stepsCount) * 100}
          filledBackground={theme.colors.primary}
          unfilledBackground={theme.colors.textRegular}
        />
      </ProgressWrapper>
    </FooterWrapper>
  );
}

export default function TypeForm({
  totalSteps,
  currentStep,
  questions,
  currentIndex,
  onIncrementSurvey,
  onDecrementSurvey,
  onAnswerQuestion,
  onSubmitAnswers,
  isLoading = false,
}) {
  const parent = React.useRef(null);

  const isLastQuestion = currentIndex === questions.length - 1;
  const isFirstQuestion = currentIndex === 0;

  const consumeThrottled = useThrottledConsumer(1000);

  React.useEffect(() => {
    parent.current &&
      autoAnimate(parent.current, {
        duration: 500,
        easing: 'ease-in-out',
      });
  }, [parent]);

  const submitAnswers = () =>
    consumeThrottled(async () => {
      await onSubmitAnswers();
    });

  const onNext = () =>
    consumeThrottled(async () => {
      if (isLastQuestion) {
        await submitAnswers();
      } else {
        onIncrementSurvey();
      }
    });

  const onPrev = () =>
    consumeThrottled(() => {
      if (isFirstQuestion) {
        return;
      } else {
        onDecrementSurvey();
      }
    });

  return (
    <Wrapper>
      <AnimWrapper ref={parent}>
        <Question
          questions={questions}
          currentIndex={currentIndex}
          onAnswerQuestion={onAnswerQuestion}
          onNext={onNext}
          isLoading={isLoading}
          key={`teams-onboarding-question-${currentIndex}`}
        />
      </AnimWrapper>
      <Footer
        currentStep={currentIndex}
        answeredCount={currentStep}
        stepsCount={totalSteps}
        onGoBack={onPrev}
      />
    </Wrapper>
  );
}
